/**
 * Tipos de datos para el Banco Maestro de APUs (Análisis de Precios Unitarios).
 * Compatible con la estructura estándar de licitación y presupuestos de obra de MEGA.
 */

export type ItemEquipoApu = {
  id: string
  descripcion: string
  unidad: string
  tarifa: number
  rendimiento: number
  valor_unitario: number
}

export type ItemMaterialApu = {
  id: string
  id_inventario?: number | string
  codigo?: string
  descripcion: string
  unidad: string
  costo_base: number
  factor_margen_pct: number
  costo_unitario: number
  cantidad: number
  valor_parcial: number
}

export type ItemTransporteApu = {
  id: string
  descripcion: string
  volumen_o_peso: number
  rendimiento: number
  costo_tarifa: number
  valor_parcial: number
}

export type ItemManoObraApu = {
  id: string
  cargo: string
  salario_base: number
  costo_dia: number
  rendimiento: number
  horas_hombre: number
  costo_total: number
}

export type AiuApu = {
  porcentaje_admin: number
  porcentaje_imprevistos: number
  porcentaje_utilidad: number
  porcentaje_iva_utilidad: number
}

export type Apu = {
  id: string
  codigo: string
  nombre: string
  descripcion?: string
  unidad: string
  categoria: string
  equipos: ItemEquipoApu[]
  materiales: ItemMaterialApu[]
  transportes: ItemTransporteApu[]
  mano_obra: ItemManoObraApu[]
  aiu: AiuApu
  costo_equipos: number
  costo_materiales: number
  costo_transportes: number
  costo_mano_obra: number
  horas_hombre_unidad: number
  costo_directo: number
  valor_admin: number
  valor_imprevistos: number
  valor_utilidad: number
  valor_iva_utilidad: number
  valor_aiu: number
  precio_unitario_total: number
  proyecto_id?: string | null
  proyecto_nombre?: string | null
  activo: boolean
  created_at?: string
  updated_at?: string
}

export const CATEGORIAS_APU = [
  'General',
  'Puesta a Tierra',
  'Conductores',
  'Canalizaciones',
  'Luminarias',
  'Salidas Eléctricas',
  'Apantallamiento (SIPRA)',
  'Tableros de Control',
  'Alumbrado Público',
  'Equipos de Medida',
  'Certificaciones y Trámites',
] as const

export type CategoriaApu = (typeof CATEGORIAS_APU)[number]

/**
 * Recalcula todos los costos y el AIU de un APU con precisión matemática.
 */
export function recalcularApu(
  base: Omit<
    Apu,
    | 'costo_equipos'
    | 'costo_materiales'
    | 'costo_transportes'
    | 'costo_mano_obra'
    | 'horas_hombre_unidad'
    | 'costo_directo'
    | 'valor_admin'
    | 'valor_imprevistos'
    | 'valor_utilidad'
    | 'valor_iva_utilidad'
    | 'valor_aiu'
    | 'precio_unitario_total'
  >
): Apu {
  // 1. Equipos
  const equipos = (base.equipos || []).map((eq) => ({
    ...eq,
    valor_unitario: Number((Number(eq.tarifa || 0) * Number(eq.rendimiento || 0)).toFixed(2)),
  }))
  const costo_equipos = equipos.reduce((sum, eq) => sum + eq.valor_unitario, 0)

  // 2. Materiales con factor de margen
  const materiales = (base.materiales || []).map((m) => {
    const baseUnit = Number(m.costo_base || 0)
    const factor = Number(m.factor_margen_pct || 0)
    const costoUnit = Number((baseUnit * (1 + factor / 100)).toFixed(2))
    const parcial = Number((costoUnit * Number(m.cantidad || 0)).toFixed(2))
    return {
      ...m,
      costo_unitario: costoUnit,
      valor_parcial: parcial,
    }
  })
  const costo_materiales = materiales.reduce((sum, m) => sum + m.valor_parcial, 0)

  // 3. Transportes
  const transportes = (base.transportes || []).map((tr) => ({
    ...tr,
    valor_parcial: Number(
      (Number(tr.volumen_o_peso || 1) * Number(tr.rendimiento || 0) * Number(tr.costo_tarifa || 0)).toFixed(2)
    ),
  }))
  const costo_transportes = transportes.reduce((sum, tr) => sum + tr.valor_parcial, 0)

  // 4. Mano de obra
  const mano_obra = (base.mano_obra || []).map((mo) => {
    const costoTotal = Number((Number(mo.costo_dia || 0) * Number(mo.rendimiento || 0)).toFixed(2))
    const hh = Number((Number(mo.rendimiento || 0) * 8).toFixed(2))
    return {
      ...mo,
      costo_total: costoTotal,
      horas_hombre: hh,
    }
  })
  const costo_mano_obra = mano_obra.reduce((sum, mo) => sum + mo.costo_total, 0)
  const horas_hombre_unidad = mano_obra.reduce((sum, mo) => sum + mo.horas_hombre, 0)

  // Costo Directo Total
  const costo_directo = Number(
    (costo_equipos + costo_materiales + costo_transportes + costo_mano_obra).toFixed(2)
  )

  // 5. Costos Indirectos (AIU)
  const adminPct = Number(base.aiu?.porcentaje_admin || 0) / 100
  const imprevPct = Number(base.aiu?.porcentaje_imprevistos || 0) / 100
  const utilPct = Number(base.aiu?.porcentaje_utilidad || 0) / 100
  const ivaUtilPct = Number(base.aiu?.porcentaje_iva_utilidad || 19) / 100

  const valor_admin = Number((costo_directo * adminPct).toFixed(2))
  const valor_imprevistos = Number((costo_directo * imprevPct).toFixed(2))
  const valor_utilidad = Number((costo_directo * utilPct).toFixed(2))
  const valor_iva_utilidad = Number((valor_utilidad * ivaUtilPct).toFixed(2))

  const valor_aiu = Number((valor_admin + valor_imprevistos + valor_utilidad + valor_iva_utilidad).toFixed(2))
  const precio_unitario_total = Number((costo_directo + valor_aiu).toFixed(2))

  return {
    ...base,
    equipos,
    materiales,
    transportes,
    mano_obra,
    costo_equipos,
    costo_materiales,
    costo_transportes,
    costo_mano_obra,
    horas_hombre_unidad,
    costo_directo,
    valor_admin,
    valor_imprevistos,
    valor_utilidad,
    valor_iva_utilidad,
    valor_aiu,
    precio_unitario_total,
  }
}

